/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import React, { forwardRef, useEffect, useRef } from "react";
import IconComponent from "@CubeComponents/icons";
// import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { Box, Menu, MenuItem } from "@mui/material";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { fileViewModes } from "./constants";
import { getIconbasedOnResourceType } from "@CubeComponents/constants/globalFunctions";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import Text from "@CubeComponents/html/Text/Text";
import useFileManagerFiles from "./useFileManagerFiles";
import type { SlickgridReactInstance } from "slickgrid-react";
import type {
  GridRowBase,
  SlickgridReactInstanceX,
  ToolbarItem,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import type { ContextMenuItem, FileManagerHandle } from "./FileManagerComponent";
import type {
  FileManagerData,
  FileManagerRequestArgs,
  FileManagerRow,
  FileManagerStateUpdate,
} from "./fileSystemServer.schema";

/** A file-manager row once it is a grid row (adds SlickGrid's own row markers). */
type FileGridRow = GridRowBase & FileManagerRow;

/** A context-menu entry as this component renders it; the file manager also sets `disabled`. */
type FileContextMenuItem = ContextMenuItem & { disabled?: boolean };

interface FilemanagerFileProps {
  setSelectedFolder(folder: FileManagerRow): void;
  onFileClick(clickedRow: FileManagerRow): void;
  onFileSelect(files: FileManagerRow[]): void;
  fileManagerData: FileManagerData;
  contextMenuSettings: {
    file: ContextMenuItem[];
    folder: ContextMenuItem[];
    layout: ContextMenuItem[];
    visible: boolean;
  };
  menuOpen(selectedFiles?: FileManagerRow[]): string[];
  /** Built against the caller's own row type; only forwarded to the grid. */
  fileColumns: object[];
  defaultPinnedColumns?: string[];
  tempFolder: FileManagerRow;
  /** `null` is the "no active search" sentinel the debounce state starts in. */
  handleSearchFiles(query: string | null): void;
  onFileLoad(files: FileManagerRow[]): void;
  beforeSending(args: FileManagerRequestArgs): void;
  allowDragAndDropUpload: boolean;
  selectedFolder: FileManagerRow;
  filesViewMode: string;
  setSelectedFilesInIconsMode(update: FileManagerStateUpdate<FileManagerRow[]>): void;
  selectedFilesInIconsMode: FileManagerRow[];
  setIsViewRendered: (rendered: boolean) => void;
  isViewRendered: boolean;
  disableDragAndDropUpload: boolean;
  preSearchText?: string;
  // Project ("Folders") mode: host the grid's own toolbar in a shared slot above the tree + grid, and feed
  // it the file-manager toolbar items. When absent, the grid toolbar stays hidden (single-space/popup).
  toolbarContainer?: HTMLElement | null;
  commonToolbar?: ToolbarItem[];
  commonToolbarClickHandler?: (id: string) => void;
  // Project ("Folders") mode: the grid sits inside a shared bordered card, so drop its own outer border.
  disableOuterBorder?: boolean;
}

const Files = forwardRef(
  (
    {
      setSelectedFolder,
      onFileClick = () => {},
      onFileSelect = () => {},
      fileManagerData = {},
      contextMenuSettings,
      menuOpen,
      fileColumns,
      defaultPinnedColumns,
      tempFolder,
      handleSearchFiles = () => {},
      onFileLoad = () => {},
      beforeSending,
      allowDragAndDropUpload,
      selectedFolder,
      filesViewMode = "DETAILED_FILES",
      setSelectedFilesInIconsMode,
      selectedFilesInIconsMode,
      setIsViewRendered,
      isViewRendered,
      disableDragAndDropUpload,
      preSearchText = "",
      toolbarContainer,
      commonToolbar = [],
      commonToolbarClickHandler,
      disableOuterBorder,
    }: FilemanagerFileProps,
    ref: React.Ref<FileManagerHandle>,
  ) => {
    const tempRef = useRef<FileManagerHandle>({});
    const dataGrid = (ref || tempRef) as React.RefObject<FileManagerHandle | null>;
    const {
      // State
      disabledRows,
      // breadCrumbData,
      filesData,
      columnDefinitions,
      menuState,
      isOver,

      // Refs

      // Handlers
      setSelectedRows,
      recordClick,
      selectionHandlerSlickGrid,
      searchFileHandler,
      handleClose,
      contextMenuForIconsView,
      handleGridCreated,
      onContextMenu,
      onDragEnter,
      onDragLeave,
      onDragOver,
      handleDrop,
      handleMenuClick,
      checkIconBoxSelect,

      // Translation
      t,
    } = useFileManagerFiles({
      setSelectedFolder,
      onFileClick,
      onFileSelect,
      fileManagerData,
      contextMenuSettings,
      menuOpen,
      fileColumns,
      tempFolder,
      handleSearchFiles,
      onFileLoad,
      beforeSending,
      allowDragAndDropUpload,
      selectedFolder,
      filesViewMode,
      setSelectedFilesInIconsMode,
      selectedFilesInIconsMode,
      setIsViewRendered,
      isViewRendered,
      disableDragAndDropUpload,
      preSearchText,
    });

    // Expose setSelectedRows via ref
    useEffect(() => {
      if (dataGrid.current) {
        dataGrid.current.setSelectedRows = (indexes: (string | number)[]) => setSelectedRows(indexes, dataGrid);
      }
    }, [dataGrid.current, filesViewMode, isViewRendered, selectedFilesInIconsMode]);

    // Restore pending selected rows after filesData updates (e.g. after refreshFiles)
    useEffect(() => {
      if (dataGrid.current?._pendingSelectedRows) {
        const pendingRows = dataGrid.current._pendingSelectedRows;
        dataGrid.current._pendingSelectedRows = null;
        dataGrid.current?.slickGrid?.setSelectedRows(pendingRows);
      }
    }, [filesData, dataGrid]);

    // Reset dataGrid when switching view modes
    useEffect(() => {
      if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
        dataGrid.current = {};
      }
      setIsViewRendered(false);
    }, [filesViewMode]);

    const gridOptions = {
      enableContextMenu: false,
      enableGridMenu: false,
      enableColumnPicker: false,
      showEmptyDataMessage: true,
      emptyDataMessage: "",
      forceFitColumns: true,
      enableAutoSizeColumns: true,
    };

    const onReactGridCreated = (reactGrid: SlickgridReactInstance) => {
      handleGridCreated(reactGrid, dataGrid);

      const emptyDiv = reactGrid.slickGrid.getContainerNode().querySelector(".slick-empty-data-warning");
      if (emptyDiv) {
        emptyDiv.classList.add("h-full");
        // @ts-expect-error -- `querySelector` returns `Element | null`; `createRoot` wants `HTMLElement`. The node is always a div this component just rendered, so the narrowing is safe but only an assertion would state it — and the guard that would prove it is emitted code.
        const root = createRoot(emptyDiv);
        root.render(
          <div className="flex flex-col items-center justify-center">
            <IconComponent name="folder" className="mb-2" width="3rem" height="3rem" />
            <Text className="not-italic">{t("This folder is empty")}</Text>
          </div>,
        );
      }
    };

    const getDetailsGrid = () => {
      const props = disableDragAndDropUpload
        ? {}
        : {
            onDragEnter: onDragEnter,
            onDragOver: onDragOver,
            onDragLeave: onDragLeave,
            onDrop: handleDrop,
          };
      return (
        <Box id="fileManagerDetailsView" className={`w-full h-full`} {...props}>
          {isOver && (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center border-4 border-dashed border-blue-500 rounded-md">
              <span className="px-4 py-2 rounded-md bg-white/70 text-blue-600 font-semibold shadow-sm">
                <Text>{t("Drop files here")}</Text>
              </span>
            </div>
          )}
          <Box className={`w-full h-full transition ${isOver ? "opacity-30" : ""}`} onContextMenu={onContextMenu}>
            <SlickGrid1
              // The same ref doubles as the file-manager handle (the effects above bolt
              // `setSelectedRows`/`_pendingSelectedRows` onto it), so it is stated as both here.
              ref={dataGrid as typeof dataGrid & React.RefObject<SlickgridReactInstanceX<FileGridRow> | null>}
              disabledIds={disabledRows}
              disableContextMenu
              toolbarHeight={15}
              enableSelectAllCheckbox
              gridOptions={gridOptions}
              disableGridMargin
              onReactGridCreated={onReactGridCreated}
              onSelectedRowIdsChanged={() => selectionHandlerSlickGrid(dataGrid)}
              // The hook holds the columns as opaque objects (they are built against the caller's own
              // row type and only forwarded); brand them here, at the grid boundary.
              defaultPinnedColumns={defaultPinnedColumns}
              columnDefinitions={columnDefinitions as ValidatedColumnDefinition<FileGridRow>[]}
              dataset={filesData}
              enableSearchBar={true}
              searchCallBack={searchFileHandler}
              searchDebounceMs={0}
              toolbar={toolbarContainer ? commonToolbar : undefined}
              toolbarClickHandler={commonToolbarClickHandler}
              disableToolbar={!toolbarContainer}
              toolbarContainer={toolbarContainer}
              disableOuterBorder={disableOuterBorder}
              showToolbarBorder
              propertiesPanel={true}
              propertiesDisabled={false}
              enableSortingOption
              enableFilteringOption
              enableGroupingOption
              enableColumnChooser
              enableRefreshOption
              refreshOptionCallBack={() => commonToolbarClickHandler?.("refresh")}
            />
          </Box>
        </Box>
      );
    };

    const renderMenuItems = (items: FileContextMenuItem[] | null = []) => {
      return items?.map((item: FileContextMenuItem) => {
        return (
          <MenuItem disabled={item.disabled} key={item.id} onClick={() => handleMenuClick(item, dataGrid)}>
            <div className="flex gap-4 text-sm items-center">
              {item.icon && <IconComponent name={item.icon} width="1.5rem" height="1.5rem" />}
              {item.name}
            </div>
          </MenuItem>
        );
      });
    };

    const getFilesIconsView = () => {
      setIsViewRendered(true);
      return (
        <Box
          id="fileManagerLargeIconsView"
          // Icons view attaches the very same ref to a plain DOM node; see FileManagerHandle.
          ref={dataGrid as typeof dataGrid & React.RefObject<HTMLDivElement | null>}
          onContextMenu={onContextMenu}
          className="p-5 border-t h-full border-button_light w-full overflow-auto"
        >
          <div className="flex flex-wrap gap-10 w-full overflow-auto">
            {filesData.map((file: FileGridRow) => (
              <Box
                key={file.id}
                onDoubleClick={() => recordClick(file)}
                onContextMenu={(e) => contextMenuForIconsView(e, file)}
                className={`group flex relative flex-col p-2 h-max pt-3 rounded overflow-hidden cursor-pointer text-ellipsis whitespace-nowrap gap-1 w-20 ${selectedFilesInIconsMode.find((item: FileManagerRow) => item.id == file.id) ? "bg-card_selection_color" : "hover:bg-card_hover_color"} ${(disabledRows as (string | undefined)[]).includes(file.id) || file.isRowDisabled ? "opacity-40 pointer-events-none" : ""}`}
              >
                <div
                  className={`absolute flex top-1 start-1 opacity-0 group-hover:opacity-100 transition-opacity ${selectedFilesInIconsMode.find((item: FileManagerRow) => item.id == file.id) ? "opacity-100" : ""}`}
                >
                  <CheckBoxComponent
                    onChange={(args) => checkIconBoxSelect(args, file)}
                    size="small"
                    checked={!!selectedFilesInIconsMode.find((item: FileManagerRow) => item.id == file.id)}
                    sx={{ padding: "0" }}
                  />
                </div>

                {/* @ts-expect-error -- `ResourceIconInput.resourceType` is declared required, but every file-manager row type has it optional. Widening the resolver's input is the real fix; it lives in `fileIconResolvers.ts` and is shared with the other file-manager tree. */}
                <div className="items-center justify-center flex">{getIconbasedOnResourceType(file, "h-10")}</div>

                <div className="overflow-hidden text-center text-ellipsis whitespace-nowrap">{file.name}</div>
              </Box>
            ))}
          </div>
        </Box>
      );
    };

    const getFilesAccordingToSelectedView = () => {
      if (filesViewMode == fileViewModes.DETAILED_VIEW) {
        return getDetailsGrid();
      } else if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
        return getFilesIconsView();
      }
    };

    const getFilesAndTools = () => (
      <div className="flex items-center flex-col h-full w-full">
        {/* {getToolBar()} */}
        {getFilesAccordingToSelectedView()}
      </div>
    );

    return (
      <div className="w-full h-full">
        {getFilesAndTools()}
        <Menu
          id="demo-positioned-menu"
          anchorReference="anchorPosition"
          // `position` is null while the menu is closed, when MUI never reads it.
          anchorPosition={menuState.position!}
          open={menuState.open}
          onClose={handleClose}
        >
          {renderMenuItems(menuState.options)}
        </Menu>
      </div>
    );
  },
);

Files.displayName = "Files";
export default Files;
